/**
 * BACKOFFICE LOGISTIQUE — Mappers purs (#662).
 * Fonctions pures doc Firestore → modèles backoffice, extraites telles quelles
 * du god-service `backoffice.service.ts` (workstream C, plan
 * architecture-cleanup-fat-components). Aucune dépendance Angular/DI :
 * entrée → sortie, testables en Karma sans TestBed.
 */

import {
  ShipmentListItem,
  ReceptionSearchResult,
  ShippingContainer,
  POST_WAREHOUSE_PHASES
} from '../models/backoffice.models';
import { receptionDone, weighingDone } from '../utils/step-done';

// All known phases — must cover every possible currentPhase value in Firestore
// Backend uses TWO naming conventions:
//   - ShipmentPhase enum: dpd_collection, collected_europe, befret_transit, congo_distribution
//   - shipment-status-initializer: dpd_phase, befret_phase
// We must query BOTH to avoid missing shipments
// #1121 — `vm_consolidation` retirée (phase jamais écrite par le serveur,
// reliquat #424) ; `vm_unidentified` ajoutée (colis VM enregistrés sans compte
// rattaché, écrits par registerIncomingPackage — avant #1121 ils n'étaient
// couverts par aucune lecture de liste).
// #1401 — MIROIR de `SHIPMENT_PHASES`
// (`functions/src/domains/shipment/shipment-phase.ts`), qui est la table que le
// serveur ÉCRIT. Les deux copies sont verrouillées dans les deux sens par
// `backoffice-phase-parity.spec.ts` (ici) et `shipment-phase.spec.ts` (serveur).
// Seuls `dpd_phase` / `befret_phase` n'y figurent pas : alias legacy, lus mais
// plus jamais écrits.
// #1153 — cette liste est le RÉFÉRENTIEL de ce qui doit rester visible : la
// réunion des `phases` de `SHIPMENT_LIST_TABS` lui est égale, et c'est vérifié
// par `models/shipment-list-tabs.spec.ts`. Ajouter une phase ici sans lui donner
// un onglet la rend invisible de la liste Colis ; l'inverse crée un onglet mort.
export const ALL_PHASES = [
  'preparation', 'order',
  'dpd_collection', 'dpd_phase', 'collected_europe',
  'warehouse',
  'befret_transit', 'befret_phase', 'congo_distribution',
  'delivered',
  'vm_reception', 'vm_unidentified',
  'heavy_processing', 'heavy_collection', 'heavy_delivery',
  'return_to_sender',
  // #1401 — `incident` était RENDUE par `derivePhaseFromStatus` (perdu,
  // endommagé, mal emballé, marchandise dangereuse, saisi en douane) mais
  // absente d'ici : le colis n'appartenait à aucun onglet, donc à aucune liste.
  // Constaté sur acceptance (#1343) : 8 colis sur 64 portaient une phase hors
  // référentiel. Le serveur écrit désormais cette phase explicitement
  // (`functions/src/domains/shipment/shipment-phase.ts`).
  'incident'
];

/**
 * Groupe PRODUIT d'un colis — les trois familles que le métier compte et que
 * l'agent cherche (standard / lourd / Virtual Mailbox). C'est une dimension
 * ORTHOGONALE à la phase : un colis VM payé est en phase `warehouse` comme un
 * standard, mais reste un colis VM.
 *
 * #1343 — la classification vivait en dur dans `getStats` (compteurs du
 * dashboard) et NULLE PART ailleurs : les cartes « Colis standard / Colis
 * lourds / Virtual Mailbox » comptaient par produit mais ouvraient la liste
 * ENTIÈRE, faute de filtre correspondant. Une seule définition ici, partagée
 * par le compteur et par le filtre, sinon les deux divergent.
 */
export type ShipmentTypeGroup = 'standard' | 'heavy' | 'vm';

export const SHIPMENT_TYPE_GROUPS: ShipmentTypeGroup[] = ['standard', 'heavy', 'vm'];

export function classifyShipmentType(type: string | undefined): ShipmentTypeGroup {
  if (type === 'virtual_mailbox') return 'vm';
  if ((type || '').startsWith('heavy')) return 'heavy';
  return 'standard';
}

// Derive phase from status.current for legacy docs without currentPhase
export function derivePhaseFromStatus(status: string | undefined): string | null {
  if (!status) return null;
  const map: Record<string, string> = {
    // Order / Payment
    'order_created': 'order', 'payment_pending': 'order', 'payment_completed': 'order',
    'payment_processing': 'order', 'payment_failed': 'order', 'draft': 'order',
    // Webhook / Shipment creation
    'webhook_pending': 'order', 'webhook_failed': 'order', 'webhook_retrying': 'order',
    'dpd_creation_failed': 'order', 'label_generation_failed': 'order',
    'shipment_created': 'dpd_collection', 'dpd_shipment_created': 'dpd_collection',
    'label_generated': 'dpd_collection',
    // DPD Collection
    'ready_for_deposit': 'dpd_collection', 'collection_scheduled': 'dpd_collection',
    'deposited_at_pickup': 'dpd_collection', 'collected_by_dpd': 'dpd_collection',
    'collection_failed': 'dpd_collection',
    // Transit EU
    'dpd_transit_eu': 'collected_europe', 'dpd_customs_check': 'collected_europe',
    'arriving_befret': 'collected_europe', 'dpd_delivery_exception': 'collected_europe',
    // Warehouse
    'received_at_warehouse': 'warehouse', 'warehouse_processing': 'warehouse',
    'warehouse_sorted': 'warehouse', 'warehouse_palletized': 'warehouse',
    'ready_for_befret': 'warehouse', 'warehouse_issue': 'warehouse',
    // Befret transit
    'befret_transit': 'befret_transit', 'customs_clearance': 'befret_transit',
    'customs_held': 'befret_transit', 'customs_cleared': 'befret_transit',
    // Congo distribution
    'local_sorting': 'congo_distribution', 'out_for_delivery': 'congo_distribution',
    'delivery_attempted': 'congo_distribution', 'awaiting_pickup': 'congo_distribution',
    // Congo distribution
    // Heavy parcel
    'heavy_request_received': 'heavy_processing', 'heavy_analysis_in_progress': 'heavy_processing',
    'heavy_quote_prepared': 'heavy_processing', 'heavy_quote_sent': 'heavy_processing',
    'heavy_quote_accepted': 'heavy_processing', 'heavy_quote_payment_pending': 'heavy_processing',
    'heavy_quote_paid': 'heavy_processing',
    'heavy_specialist_assigned': 'heavy_collection', 'heavy_collection_scheduled': 'heavy_collection',
    'heavy_collected': 'heavy_collection', 'heavy_in_transit': 'heavy_collection',
    'heavy_at_warehouse': 'warehouse',
    'heavy_out_for_delivery': 'heavy_delivery', 'heavy_delivered': 'delivered',
    // Pre-payment (Stripe session created but not yet paid)
    'stripe_session_created': 'order',
    // #1401 — statuts que le serveur écrit et que cette table ignorait : le
    // repli rendait `null`, donc « phase inconnue » pour un colis parfaitement
    // identifiable. Alignés sur `STATUS_TO_SHIPMENT_PHASE`
    // (functions/src/domains/shipment/shipment-phase.ts).
    'vm_package_received': 'vm_reception',
    'assisted_draft': 'order', 'assisted_sms_sent': 'order',
    'assisted_expired': 'incident', 'assisted_cancelled': 'incident',
    'cancelled': 'incident',
    // #415 — statuts backoffice (via CF #399), avant hors mapping
    'destination_sorted': 'warehouse', 'preparation_blocked': 'warehouse',
    'delivery_sorted': 'congo_distribution', 'delivery_rescheduled': 'congo_distribution',
    // #405 — incidents Europe
    'dpd_incident_eu': 'collected_europe', 'held_at_dpd_depot': 'collected_europe',
    // Terminal — #415 : perdu/endommagé/saisi ≠ « Livré » (phase incident
    // distincte pour les stats/listes staff)
    'delivered': 'delivered', 'returned_to_sender': 'return_to_sender',
    // #410 aval — cycle retour, même phase staff que returned_to_sender
    'return_received_at_warehouse': 'return_to_sender', 'return_completed': 'return_to_sender',
    'lost': 'incident', 'damaged': 'incident',
    'poorly_packed': 'incident', 'dangerous_goods': 'incident',
    'seized_customs_eu': 'incident', 'seized_customs_congo': 'incident',
  };
  return map[status] || null;
}

/**
 * #416 — normalisation du code-barres DPD scanné.
 * Le CODE_128 physique DPD contient plus que le parcelLabelNumber stocké
 * (format ~28 chars : préfixe routage + zipCode + parcelLabelNumber(14)
 * + code service(3) + code pays(3)). Un scan brut ne matche donc jamais
 * le champ Firestore (14 chiffres) en égalité stricte. On dérive des
 * candidats : valeur brute, chiffres seuls, et les 14 chiffres du
 * parcelLabelNumber extraits du scan complet.
 */
export function dpdCodeCandidates(raw: string): string[] {
  const trimmed = raw.trim();
  const candidates = [trimmed];
  const digits = trimmed.replace(/\D/g, '');
  if (digits && digits !== trimmed) candidates.push(digits);
  if (digits.length >= 20) {
    // Scan complet : suffixe = service(3) + pays(3) ; les 14 chiffres
    // précédents = parcelLabelNumber.
    candidates.push(digits.slice(-20, -6));
  }
  return [...new Set(candidates)].filter(c => !!c);
}

/**
 * Extract createdAt from a shipment document, checking all known field paths.
 * Different shipment types store it in different places:
 *   - timestamps.createdAt (standard convention — assisted, VM, portal)
 *   - metadata.createdAt (unified migration service — standard shipments)
 *   - createdAt (root level — some legacy)
 *   - timestamps.created (rare legacy)
 *   - heavyData.requestedAt (heavy parcels — may be the only date available)
 *   - paymentFlow.paidAt (fallback — at least we know when payment happened)
 *   - la plus ancienne entrée de statusHistory (dernier recours)
 *
 * #2122 — `null` si aucun champ n'est lisible, jamais la date du jour : un colis
 * ancien passerait pour créé aujourd'hui et remonterait en tête de liste.
 */
export function extractCreatedAt(data: any): Date | null {
  const candidates = [
    data['timestamps']?.['createdAt'],
    data['metadata']?.['createdAt'],
    data['createdAt'],
    data['timestamps']?.['created'],
    data['heavyData']?.['requestedAt'],
    data['paymentFlow']?.['paidAt'],
    data['assistedData']?.['createdAt'],
  ];

  for (const val of candidates) {
    const date = toDateOrNull(val);
    if (date) return date;
  }

  const history: unknown = data['statusHistory'];
  if (!Array.isArray(history)) return null;
  const dates = history
    .map(entry => toDateOrNull(entry?.['timestamp']))
    .filter((date): date is Date => date !== null);
  return dates.length > 0 ? new Date(Math.min(...dates.map(date => date.getTime()))) : null;
}

/** #2122 — plus récent d'abord ; un colis sans date lisible va en fin de liste, jamais en tête. */
export function compareByCreatedAtDesc(a: { createdAt: Date | null }, b: { createdAt: Date | null }): number {
  return (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0);
}

/**
 * #1122 — convertit une valeur de date Firestore/legacy en `Date`, ou `null`.
 * L'absence reste l'absence. #2122 — lit aussi les maps `{seconds}` / `{_seconds}`.
 */
export function toDateOrNull(val: any): Date | null {
  if (!val) return null;
  if (val.toDate && typeof val.toDate === 'function') return val.toDate();
  if (val instanceof Date) return val.getTime() > 0 ? val : null;
  if (typeof val === 'string' && val.length > 0) {
    const d = new Date(val);
    return !isNaN(d.getTime()) && d.getTime() > 0 ? d : null;
  }
  if (typeof val === 'number' && val > 0) return new Date(val);
  const seconds = val['seconds'] ?? val['_seconds'];
  if (typeof seconds === 'number' && seconds > 0) return new Date(seconds * 1000);
  return null;
}

/**
 * #1354 — statuts du journal `statusHistory` qui datent l'ARRIVÉE en RDC.
 * Deux natures d'entrées cohabitent dans ce journal : des statuts de la matrice
 * (`local_sorting`) et des événements d'activité backoffice
 * (`parcel_scanned_congo`, `arrived_congo`, écrits par `buildActivityEvent`).
 * L'ordre reflète la précision décroissante : le scan du colis d'abord,
 * l'arrivée du groupage ensuite.
 */
export const ARRIVAL_SCAN_HISTORY_STATUSES = ['parcel_scanned_congo'];
export const ARRIVAL_FALLBACK_HISTORY_STATUSES = ['local_sorting', 'arrived_congo'];

/** #1354 — statuts du journal qui datent la remise au destinataire. */
export const DELIVERED_HISTORY_STATUSES = [
  'delivered', 'heavy_delivered', 'heavy_delivered_with_installation',
];

/**
 * #1354 — date de la DERNIÈRE entrée du journal portant l'un des statuts donnés.
 *
 * Le journal vit sous deux clés selon l'écrivain : `statusHistory` à la racine
 * (écrans staff + CF `transitionShipmentStatus`) et, sur quelques documents,
 * `status.history`. On lit les deux. Les timestamps y sont tantôt des
 * `Timestamp` Firestore, tantôt des chaînes ISO — `toDateOrNull` absorbe les
 * deux et refuse tout ce qui ne se convertit pas (garde contre les scories
 * d'affichage type « 36500 jours », #1116).
 *
 * « Dernière » et non « première » : un colis peut repasser par le même statut
 * (retrait reprogrammé, seconde tentative), et c'est le geste le plus récent
 * qui fait foi.
 */
export function lastStatusHistoryDate(data: any, statuses: string[]): Date | null {
  const journals = [data?.['statusHistory'], data?.['status']?.['history']];
  let latest: Date | null = null;
  for (const journal of journals) {
    if (!Array.isArray(journal)) continue;
    for (const entry of journal) {
      if (!entry || !statuses.includes(entry['status'])) continue;
      const d = toDateOrNull(entry['timestamp']);
      if (d && (!latest || d.getTime() > latest.getTime())) latest = d;
    }
  }
  return latest;
}

/**
 * #1354 — date de scan à l'arrivée en RDC, ou `null`.
 *
 * ⚠️ Il n'existe AUCUN champ unique : mesuré le 21/08/2026 sur les documents en
 * phase de distribution, `congoArrival.scannedAt` est présent sur 5/12 docs en
 * dev, 3/9 en acceptance et **0/36 en production** — les colis de production
 * ont été pilotés par les écrans legacy, qui ne posent aucun de ces champs et
 * ne laissent une trace que dans `statusHistory` (35/36). Sans ce repli, la
 * colonne demandée par le ticket serait vide sur la quasi-totalité du parc.
 */
export function extractArrivalScanAt(data: any): Date | null {
  return toDateOrNull(data?.['congoArrival']?.['scannedAt'])
    ?? lastStatusHistoryDate(data, ARRIVAL_SCAN_HISTORY_STATUSES)
    ?? toDateOrNull(data?.['congoArrival']?.['arrivedAt'])
    ?? toDateOrNull(data?.['timestamps']?.['congoArrivedAt'])
    ?? lastStatusHistoryDate(data, ARRIVAL_FALLBACK_HISTORY_STATUSES);
}

/**
 * #1354 — date de retrait / livraison au destinataire, ou `null`.
 *
 * Trois écrivains posent trois champs différents : la CF de transition
 * (`timestamps.deliveredAt` + `delivery.proof.deliveredAt`), l'app livreur
 * (`delivery.deliveredAt`, chaîne ISO) et les écrans legacy (rien du tout —
 * seulement une entrée `delivered` dans `statusHistory`). Mesuré le 21/08/2026 :
 * 0/34 colis livrés de production portent `timestamps.deliveredAt`, 34/34
 * portent l'entrée de journal.
 *
 * `null` est un résultat légitime : un colis pas encore retiré n'a pas de date,
 * et la vue affiche « — » plutôt que d'inventer un instant.
 */
export function extractDeliveredAt(data: any): Date | null {
  return toDateOrNull(data?.['timestamps']?.['deliveredAt'])
    ?? toDateOrNull(data?.['delivery']?.['proof']?.['deliveredAt'])
    ?? toDateOrNull(data?.['delivery']?.['deliveredAt'])
    ?? lastStatusHistoryDate(data, DELIVERED_HISTORY_STATUSES);
}

export function mapToListItem(docSnap: any): ShipmentListItem {
  const data = docSnap.data();
  const sender = data['customerInfo']?.['sender'] || {};
  const receiver = data['customerInfo']?.['receiver'] || {};
  const senderAddr = sender['address'] || {};
  const receiverAddr = receiver['address'] || {};

  // #1121 — hydratation VM sur les lignes de LISTE (avant : réservée à
  // mapToReceptionResult) pour la recherche par boîte/marchand. Type
  // canonique-first (T25-W) avec fallback legacy root.
  const isVm = (data['parcelInfo']?.['type'] || data['type']) === 'virtual_mailbox';
  const vmData = data['virtualMailboxData'] || {};
  const vmAccount = vmData['account'] || {};
  const vmIncoming = vmData['incomingPackage'] || null;

  // #1354 — calculées une fois : les deux repli-chaînes lisent le journal.
  const arrivalScanAt = extractArrivalScanAt(data);
  const deliveredAt = extractDeliveredAt(data);

  return {
    id: docSnap.id,
    trackingNumber: data['trackingNumber'] || '',
    dpdTrackingNumber: data['standardData']?.['dpdTrackingNumber']
      || data['dpdIntegration']?.['trackingNumber'] || '',
    type: data['type'] || '',
    category: data['category'] || 'standard',
    currentPhase: data['currentPhase'] || '',
    statusCurrent: data['status']?.['current'] || '',
    senderName: sender['name'] || `${sender['firstName'] || ''} ${sender['lastName'] || ''}`.trim() || '-',
    senderEmail: sender['email'] || '',
    senderCountry: senderAddr['country'] || senderAddr['countryCode'] || '',
    receiverName: receiver['name'] || `${receiver['firstName'] || ''} ${receiver['lastName'] || ''}`.trim() || '-',
    receiverCity: receiverAddr['city'] || data['logistics']?.['destinationCity'] || data['destination']?.['city'] || '',
    weight: data['parcelInfo']?.['weight'] || data['virtualMailboxData']?.['incomingPackage']?.['actualWeight'] || data['weight'] || 0,
    befretWeight: data['warehouse']?.['befretWeight'],
    deliveryMethod: data['serviceConfig']?.['befretDeliveryMethod']
      || data['standardData']?.['befretDeliveryMethod'] || '',
    depositMethod: data['serviceConfig']?.['dpdServiceType'] || '',
    emballage: data['parcelInfo']?.['options']?.['reinforcedPackaging']
      || data['serviceConfig']?.['emballage'] || false,
    createdAt: extractCreatedAt(data),
    paymentStatus: data['paymentFlow']?.['paymentStatus'] || '',
    weightDiscrepancyStatus: data['weightDiscrepancy']?.['status'] || '',
    weightDiscrepancyType: data['weightDiscrepancy']?.['type'] || '',
    weightDiscrepancyAmount: data['weightDiscrepancy']?.['amount'] || 0,
    destinationZone: data['preparation']?.['destinationZone'] || '',
    transportMode: data['preparation']?.['transportMode'] || '',
    sortedAt: toDateOrNull(data['preparation']?.['sortedAt']),
    preparationCondition: data['preparation']?.['condition'] || 'ok',
    assignedPartnerId: data['delivery']?.['partnerId'] || '',
    assignedPartnerName: data['delivery']?.['partnerName'] || '',
    // #1354 — dates de la vue « Livraisons ». Même discipline que #1122 : clé
    // OMISE quand la date n'existe pas, jamais posée à `undefined`.
    ...(arrivalScanAt ? { arrivalScanAt } : {}),
    ...(deliveredAt ? { deliveredAt } : {}),
    // Champs optionnels : jamais `undefined` explicite pour un colis VM
    // (chaîne vide en défaut), absents sinon.
    ...(isVm ? {
      vmBoxNumber: vmAccount['boxNumber'] || vmData['boxNumber'] || '',
      vmMerchant: vmIncoming?.['merchant'] || '',
      // #1122 — dates de référence du couloir VM (ancienneté de la vue
      // « Travail VM du jour »). Clés OMISES quand la date n'existe pas,
      // jamais posées à `undefined` (même discipline que #1121).
      ...(toDateOrNull(vmIncoming?.['receivedAt'])
        ? { vmReceivedAt: toDateOrNull(vmIncoming?.['receivedAt'])! } : {}),
      ...(toDateOrNull(vmData['shippingRequest']?.['paidAt'])
        ? { vmPaidAt: toDateOrNull(vmData['shippingRequest']?.['paidAt'])! } : {}),
      // #1344 — la commande client elle-même (statut, montant, date de demande).
      // C'est ce que l'écran doit montrer et sur quoi il doit juger l'attente de
      // paiement : `status.current` n'est qu'un miroir écrasable.
      ...(vmData['shippingRequest']?.['status']
        ? { vmRequestStatus: vmData['shippingRequest']['status'] } : {}),
      ...(typeof vmData['shippingRequest']?.['pricing']?.['total'] === 'number'
        ? { vmRequestTotal: vmData['shippingRequest']['pricing']['total'] } : {}),
      ...(vmData['shippingRequest']?.['pricing']?.['currency']
        ? { vmRequestCurrency: vmData['shippingRequest']['pricing']['currency'] } : {}),
      ...(toDateOrNull(vmData['shippingRequest']?.['requestedAt'])
        ? { vmRequestedAt: toDateOrNull(vmData['shippingRequest']?.['requestedAt'])! } : {}),
      // #1395 — instant où le devis est MORT (`recordVmMeasurement`). C'est
      // depuis lui que court l'attente d'une relance, pas depuis l'arrivée du
      // colis : un colis stocké six semaines puis expiré ce matin n'est pas
      // « en retard » de six semaines de relance.
      ...(toDateOrNull(vmData['shippingRequest']?.['expiredAt'])
        ? { vmRequestExpiredAt: toDateOrNull(vmData['shippingRequest']?.['expiredAt'])! } : {}),
      // 🔴 #1837 — émission de la facture VM. Clé OMISE tant qu'elle n'est pas
      // émise : c'est cette absence que la file « Reçus, à traiter » lit.
      ...(toDateOrNull(vmData['storage']?.['invoiceIssuedAt'])
        ? { vmInvoiceIssuedAt: toDateOrNull(vmData['storage']?.['invoiceIssuedAt'])! } : {}),
      // #2158 — l'e-mail de facture est-il parti ? Clé OMISE sinon : c'est cette
      // absence que lit l'anomalie « liens de paiement non envoyés ».
      ...(toDateOrNull(vmData['storage']?.['invoiceNotifiedAt'])
        ? { vmInvoiceNotifiedAt: toDateOrNull(vmData['storage']?.['invoiceNotifiedAt'])! } : {}),
    } : {}),
  };
}

export function mapToReceptionResult(docSnap: any): ReceptionSearchResult {
  const data = docSnap.data();
  const sender = data['customerInfo']?.['sender'] || {};
  const receiver = data['customerInfo']?.['receiver'] || {};
  const receiverAddr = receiver['address'] || {};
  const phase = data['currentPhase'] || '';
  // T25-W PR4 : canonique-first (parcelInfo.type) avec fallback legacy root pour
  // les vieux docs. Les écritures assisted/vm/portal ne produisent plus de root `type`.
  const type = data['parcelInfo']?.['type'] || data['type'] || '';

  const senderPhone = typeof sender['phone'] === 'object'
    ? `${sender['phone']['prefix'] || ''} ${sender['phone']['number'] || ''}`.trim()
    : (sender['phone'] || '');

  // VM-specific data
  const vmData = data['virtualMailboxData'] || {};
  const vmIncoming = vmData['incomingPackage'] || null;
  const vmAccount = vmData['account'] || {};
  const isVm = type === 'virtual_mailbox';

  const result: ReceptionSearchResult = {
    id: docSnap.id,
    trackingNumber: data['trackingNumber'] || '',
    dpdTrackingNumber: data['standardData']?.['dpdTrackingNumber']
      || data['dpdIntegration']?.['trackingNumber'] || '',
    senderName: sender['name'] || `${sender['firstName'] || ''} ${sender['lastName'] || ''}`.trim() || '-',
    senderEmail: sender['email'] || '',
    senderPhone,
    receiverName: receiver['name'] || `${receiver['firstName'] || ''} ${receiver['lastName'] || ''}`.trim() || '-',
    receiverCity: receiverAddr['city'] || data['logistics']?.['destinationCity'] || '',
    weight: data['parcelInfo']?.['weight'] || vmIncoming?.['actualWeight'] || data['weight'] || 0,
    currentPhase: phase,
    currentStatus: data['status']?.['current'] || '',
    type,
    createdAt: extractCreatedAt(data),
    isAlreadyReceived: POST_WAREHOUSE_PHASES.includes(phase),
    receivedDone: receptionDone(data),
    weighedDone: weighingDone(data),
    // #403 — visibilité paiement + traitement post-paiement au scan (le gate
    // bloquant est appliqué serveur par transitionShipmentStatus).
    paymentStatus: data['paymentFlow']?.['paymentStatus'] || '',
    webhookOverallStatus: data['webhookStatus']?.['overallStatus'] || '',
  };

  // Populate VM-specific fields
  if (isVm) {
    // #1396 — l'autorité de paiement voyage avec le résultat de scan.
    result.vmRequestStatus = vmData['shippingRequest']?.['status'];
    result.vmBoxNumber = vmAccount['boxNumber'] || vmData['boxNumber'] || '';
    result.vmBoxOwnerName = vmAccount['customerName'] || '';
    result.vmRegistered = !!vmIncoming;
    result.vmPhotosCount = vmIncoming?.['photos'] ? Object.keys(vmIncoming['photos']).filter((k: string) => !!vmIncoming['photos'][k]).length : 0;
    result.vmExternalCarrier = vmIncoming?.['externalCarrier'] || '';
    result.vmExternalTracking = vmIncoming?.['externalTracking'] || '';
    result.vmMerchant = vmIncoming?.['merchant'] || '';
    result.vmAlreadyWeighed = !!(vmIncoming?.['actualWeight'] && vmIncoming['actualWeight'] > 0);
  }

  // Populate heavy-specific fields
  if (type === 'heavy_custom' || type === 'heavy_specialist') {
    result.heavyCategory = type;
  }

  return result;
}

export function mapToContainer(docSnap: any): ShippingContainer {
  const data = docSnap.data();
  const toDate = (val: any): Date => val?.toDate?.() || (typeof val === 'string' ? new Date(val) : new Date());

  const mapContactSlot = (slot: any) => slot ? {
    memberId: slot['memberId'] || '',
    memberName: slot['memberName'] || '',
    confirmedAt: slot['confirmedAt'] ? toDate(slot['confirmedAt']) : undefined,
  } : undefined;

  return {
    id: docSnap.id,
    containerNumber: data['containerNumber'] || '',
    transportMode: data['transportMode'] || 'air',
    maxWeightKg: data['maxWeightKg'] || 23,
    maxWeightType: data['maxWeightType'],
    currentWeightKg: data['currentWeightKg'] || 0,
    status: data['status'] || 'open',
    shipmentIds: data['shipmentIds'] || [],
    shipmentCount: data['shipmentCount'] || 0,
    destinationCity: data['destinationCity'] || '',
    departure: {
      closedAt: data['departure']?.['closedAt'] ? toDate(data['departure']['closedAt']) : undefined,
      closedBy: data['departure']?.['closedBy'],
      departureWeight: data['departure']?.['departureWeight'] || 0,
      sealNumber: data['departure']?.['sealNumber'],
      dispatchedAt: data['departure']?.['dispatchedAt'] ? toDate(data['departure']['dispatchedAt']) : undefined,
      estimatedArrival: data['departure']?.['estimatedArrival'] ? toDate(data['departure']['estimatedArrival']) : undefined,
    },
    contactChain: data['contactChain'] || [],
    shippingMethodDetails: data['shippingMethodDetails'] || undefined,
    filmage: data['filmage'] || undefined,
    containerLabelUrl: data['containerLabelUrl'] || undefined,
    groupagePhotoUrl: data['groupagePhotoUrl'] || undefined,
    expeditionContacts: data['expeditionContacts'] ? {
      responsableGroupageA: mapContactSlot(data['expeditionContacts']['responsableGroupageA']),
      contactExpedition: mapContactSlot(data['expeditionContacts']['contactExpedition']),
      contactReception: mapContactSlot(data['expeditionContacts']['contactReception']),
      responsableGroupageB: mapContactSlot(data['expeditionContacts']['responsableGroupageB']),
      openedBy: data['expeditionContacts']['openedBy'],
      openedAt: data['expeditionContacts']['openedAt'] ? toDate(data['expeditionContacts']['openedAt']) : undefined,
      closedBy: data['expeditionContacts']['closedBy'],
      closedAt: data['expeditionContacts']['closedAt'] ? toDate(data['expeditionContacts']['closedAt']) : undefined,
    } : undefined,
    expeditionStep: data['expeditionStep'] || 'groupage',
    arrival: data['arrival'] ? {
      arrivedAt: data['arrival']['arrivedAt'] ? toDate(data['arrival']['arrivedAt']) : undefined,
      arrivalWeight: data['arrival']['arrivalWeight'],
      weightDelta: data['arrival']['weightDelta'],
      condition: data['arrival']['condition'] || 'good',
      receivedBy: data['arrival']['receivedBy'],
      groupagePhotoUrl: data['arrival']['groupagePhotoUrl'],
      weighedAt: data['arrival']['weighedAt'] ? toDate(data['arrival']['weighedAt']) : undefined,
      weighingAlert: data['arrival']['weighingAlert'],
    } : undefined,
    unpacking: data['unpacking'] ? {
      startedAt: data['unpacking']['startedAt'] ? toDate(data['unpacking']['startedAt']) : undefined,
      completedAt: data['unpacking']['completedAt'] ? toDate(data['unpacking']['completedAt']) : undefined,
      scannedShipmentIds: data['unpacking']['scannedShipmentIds'] || [],
      missingShipmentIds: data['unpacking']['missingShipmentIds'] || [],
    } : undefined,
    createdAt: toDate(data['createdAt']),
    createdBy: data['createdBy'] || '',
    updatedAt: toDate(data['updatedAt']),
    notes: data['notes'],
  };
}
